import { useState, useEffect } from 'react';
import { BarChart3, Search, Clock, TrendingUp } from 'lucide-react';
import { api, Analytics as AnalyticsData } from '../lib/api';

export default function Analytics() {
  const [analytics, setAnalytics] = useState<AnalyticsData | null>(null);
  const [days, setDays] = useState(30);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadAnalytics();
  }, [days]);

  const loadAnalytics = async () => {
    setLoading(true);
    try {
      const data = await api.getAnalytics(days);
      setAnalytics(data);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <p className="text-red-600">{error}</p>
      </div>
    );
  }

  const totalSearches = analytics?.daily_stats.reduce((sum, d) => sum + d.search_count, 0) || 0;
  const avgDaily = analytics?.daily_stats.length ? Math.round(totalSearches / analytics.daily_stats.length) : 0;

  return (
    <div className="space-y-6">
      {/* Time period selector */}
      <div className="flex justify-between items-center">
        <p className="text-gray-600">Search analytics and usage statistics.</p>
        <select
          value={days}
          onChange={(e) => setDays(Number(e.target.value))}
          className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        >
          <option value={7}>Last 7 days</option>
          <option value={30}>Last 30 days</option>
          <option value={90}>Last 90 days</option>
        </select>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white rounded-xl shadow-sm border p-6">
          <div className="flex items-center">
            <div className="bg-blue-100 p-3 rounded-lg">
              <Search className="w-6 h-6 text-blue-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Total Searches</p>
              <p className="text-2xl font-bold text-gray-900">{totalSearches.toLocaleString()}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl shadow-sm border p-6">
          <div className="flex items-center">
            <div className="bg-green-100 p-3 rounded-lg">
              <TrendingUp className="w-6 h-6 text-green-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Avg. Daily Searches</p>
              <p className="text-2xl font-bold text-gray-900">{avgDaily.toLocaleString()}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl shadow-sm border p-6">
          <div className="flex items-center">
            <div className="bg-purple-100 p-3 rounded-lg">
              <BarChart3 className="w-6 h-6 text-purple-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Unique Queries</p>
              <p className="text-2xl font-bold text-gray-900">{analytics?.top_queries.length || 0}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Daily stats chart (simple bar representation) */}
      <div className="bg-white rounded-xl shadow-sm border p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Daily Search Volume</h3>
        {analytics?.daily_stats.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <BarChart3 className="w-12 h-12 mx-auto mb-4 text-gray-300" />
            <p>No search data for this period.</p>
          </div>
        ) : (
          <div className="space-y-2">
            {analytics?.daily_stats.slice(-14).map((stat) => {
              const maxCount = Math.max(...(analytics?.daily_stats.map(s => s.search_count) || [1]));
              const percentage = (stat.search_count / maxCount) * 100;
              return (
                <div key={stat.date} className="flex items-center gap-3">
                  <span className="text-sm text-gray-500 w-24">
                    {new Date(stat.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                  </span>
                  <div className="flex-1 bg-gray-100 rounded-full h-6 overflow-hidden">
                    <div
                      className="bg-blue-600 h-full rounded-full transition-all duration-300"
                      style={{ width: `${percentage}%` }}
                    />
                  </div>
                  <span className="text-sm font-medium text-gray-700 w-16 text-right">
                    {stat.search_count}
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Top queries */}
        <div className="bg-white rounded-xl shadow-sm border p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Top Search Queries</h3>
          {analytics?.top_queries.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <Search className="w-12 h-12 mx-auto mb-4 text-gray-300" />
              <p>No queries recorded yet.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {analytics?.top_queries.map((query, index) => (
                <div key={query.query} className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <span className="w-6 h-6 bg-gray-100 rounded-full flex items-center justify-center text-sm font-medium text-gray-600">
                      {index + 1}
                    </span>
                    <span className="text-gray-900">{query.query}</span>
                  </div>
                  <span className="text-sm text-gray-500">{query.count} searches</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Recent searches */}
        <div className="bg-white rounded-xl shadow-sm border p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Recent Searches</h3>
          {analytics?.recent_searches.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <Clock className="w-12 h-12 mx-auto mb-4 text-gray-300" />
              <p>No recent searches.</p>
            </div>
          ) : (
            <div className="space-y-3 max-h-80 overflow-y-auto">
              {analytics?.recent_searches.map((search, index) => (
                <div key={index} className="flex items-start justify-between py-2 border-b last:border-0">
                  <div>
                    <p className="text-gray-900">{search.query}</p>
                    <p className="text-xs text-gray-500">
                      {search.results_count} results
                      {search.domain && ` • ${search.domain}`}
                    </p>
                  </div>
                  <span className="text-xs text-gray-400">
                    {new Date(search.created_at).toLocaleString()}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
